<!-- components/Entity/AttributeField.vue -->
<template>
  <div class="attribute-field" :class="{ 'llm-enriched': isLlmEnriched }">
    <dt class="text-xs font-medium text-gray-500 mb-0.5 flex items-center justify-between">
      <span class="truncate" :title="attribute.display_name">{{ attribute.display_name }}</span>
      <button v-if="!isEditing && editable"
                @click="startEdit"
                class="ml-1 text-gray-400 hover:text-gray-600 shrink-0 opacity-100">
                <font-awesome-icon :icon="['fas', 'pencil']" class="text-xs" />
      </button>
    </dt>

    <dd class="text-sm text-gray-900">
      <!-- Edit Mode -->
      <div v-if="isEditing" class="flex items-center gap-2">
        <!-- Phone input -->
        <input v-if="attribute.field_type === 'phone'"
               v-model="editValue"
               type="tel"
               class="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-dark"
               :placeholder="attribute.display_name">

        <!-- Email input -->
        <input v-else-if="attribute.field_type === 'email'"
               v-model="editValue"
               type="email"
               class="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-dark"
               :placeholder="attribute.display_name">

        <!-- URL input -->
        <input v-else-if="attribute.field_type === 'url'"
               v-model="editValue"
               type="url"
               class="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-dark"
               placeholder="https://...">

        <!-- Date input -->
        <input v-else-if="attribute.field_type === 'date'"
               v-model="editValue"
               type="date"
               class="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-dark">

        <!-- Boolean checkbox -->
        <label v-else-if="attribute.field_type === 'boolean'"
               class="flex items-center gap-2">
          <input v-model="editValue"
                 type="checkbox"
                 class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark">
          <span>{{ attribute.display_name }}</span>
        </label>

        <!-- Number input -->
        <input v-else-if="attribute.field_type === 'number'"
               v-model.number="editValue"
               type="number"
               class="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-dark"
               :placeholder="attribute.display_name">

        <!-- JSON textarea -->
        <textarea v-else-if="attribute.field_type === 'json'"
                  v-model="editValueJson"
                  class="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-dark font-mono text-xs"
                  rows="4"
                  @blur="validateJson">
        </textarea>

        <!-- Default text input -->
        <input v-else
               v-model="editValue"
               type="text"
               class="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-dark"
               :placeholder="attribute.display_name">

        <!-- Save/Cancel buttons -->
        <button @click="save"
                :disabled="saving"
                class="btn-primary-sm">
          <font-awesome-icon v-if="saving" :icon="['fas', 'spinner']" spin />
          <font-awesome-icon v-else :icon="['fas', 'check']" />
        </button>
        <button @click="cancelEdit"
                class="px-2 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </div>

      <!-- Display Mode -->
      <div v-else class="flex items-center justify-between">
        <span class="flex-1">
          <!-- Phone display. In Demo Mode the visible text is spoofed by the
               engine (.sensitive); the href is dropped so the raw value can't
               leak via the link target, hover preview, or status bar. -->
          <a v-if="attribute.field_type === 'phone' && displayValue"
             :href="$root?.isDemoMode ? null : `tel:${displayValue}`"
             class="sensitive hover:text-blue-600">
            {{ formatPhone(displayValue) }}
          </a>

          <!-- Email display -->
          <a v-else-if="attribute.field_type === 'email' && displayValue"
             :href="$root?.isDemoMode ? null : `mailto:${displayValue}`"
             class="sensitive hover:text-blue-600">
            {{ displayValue }}
          </a>

          <!-- URL display -->
          <a v-else-if="attribute.field_type === 'url' && displayValue"
             :href="$root?.isDemoMode ? null : displayValue"
             target="_blank"
             class="sensitive hover:text-blue-600">
            {{ displayValue }}
            <font-awesome-icon :icon="['fas', 'external-link-alt']" class="ml-1 text-xs" />
          </a>

          <!-- Date display -->
          <span v-else-if="attribute.field_type === 'date' && displayValue">
            {{ formatDate(displayValue) }}
          </span>

          <!-- Boolean display -->
          <span v-else-if="attribute.field_type === 'boolean'">
            <span :class="displayValue ? 'text-green-600' : 'text-gray-400'">
              {{ displayValue ? '✓ Yes' : '✗ No' }}
            </span>
          </span>

          <!-- Number display -->
          <span v-else-if="attribute.field_type === 'number'">
            {{ formatNumber(displayValue) }}
          </span>

          <!-- JSON display -->
          <pre v-else-if="attribute.field_type === 'json'"
               class="text-xs bg-gray-50 p-1 rounded mt-0.5">{{ JSON.stringify(displayValue, null, 2) }}</pre>

          <!-- Default text display -->
          <span v-else>{{ displayValue || '—' }}</span>
        </span>

        <!-- Metadata indicators -->
        <div class="flex items-center gap-1 ml-2 relative">
          <span v-if="displayConfidence > 1"
                class="text-xs text-green-600"
                :title="`Confirmed by ${displayConfidence} sources`">
            ✓{{ displayConfidence }}
          </span>
          <button v-if="displayDataSource && displayDataSource !== 'Manual Entry'"
                  ref="infoButton"
                  @click="toggleMetadata"
                  class="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  :title="`Source: ${displayDataSource}\nUpdated: ${formatDate(displayUpdated)}`">
            <font-awesome-icon :icon="['fas', 'info-circle']" class="text-sm" />
          </button>

          <!-- Metadata Popup -->
          <Teleport to="body">
            <div v-if="showMetadata"
                 ref="popup"
                 :style="popupStyle"
                 class="fixed z-50 w-72 p-3 bg-white border border-gray-300 rounded-lg shadow-xl">
              <div class="space-y-2">
                <div class="flex justify-between items-start">
                  <span class="font-medium text-gray-700 text-sm">Source Information</span>
                  <button @click="showMetadata = false"
                          class="text-gray-400 hover:text-gray-600">
                    <font-awesome-icon :icon="['fas', 'times']" class="text-xs" />
                  </button>
                </div>

                <div class="space-y-1.5 text-xs">
                  <div class="flex justify-between">
                    <span class="font-medium text-gray-600">Source:</span>
                    <span class="text-gray-900">{{ displayDataSource }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="font-medium text-gray-600">Updated:</span>
                    <span class="text-gray-900">{{ formatDateTime(displayUpdated) }}</span>
                  </div>
                  <div v-if="displayConfidence" class="flex justify-between">
                    <span class="font-medium text-gray-600">Confidence:</span>
                    <span class="text-gray-900">{{ displayConfidence }} source{{ displayConfidence !== 1 ? 's' : '' }}</span>
                  </div>
                  <div v-if="displayReason" class="mt-2 pt-2 border-t border-gray-200">
                    <span class="font-medium text-gray-600 block mb-1">Reason:</span>
                    <span class="text-gray-700 block">{{ displayReason }}</span>
                  </div>
                </div>
              </div>
            </div>
          </Teleport>
        </div>
      </div>
    </dd>

    <!-- Error message -->
    <div v-if="error" class="mt-0.5 text-xs text-red-600">
      {{ error }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    entityId: { type: [String, Number], required: true },
    attribute: { type: Object, required: true },
    editable: { type: Boolean, default: true },
    showConfidence: { type: Boolean, default: false }
  },

  data() {
    return {
      isEditing: false,
      editValue: null,
      editValueJson: '',
      saving: false,
      error: null,
      showMetadata: false,
      popupStyle: {},
      // Local copies of attribute data that can be updated
      localValue: null,
      localDataSource: null,
      localUpdated: null,
      localConfidence: null,
      localReason: null,
    };
  },

  computed: {
    // Use local value if it exists (after save), otherwise use prop value
    displayValue() {
      return this.localValue !== null ? this.localValue : this.attribute.value;
    },
    displayDataSource() {
      return this.localDataSource !== null ? this.localDataSource : this.attribute.data_source;
    },
    displayUpdated() {
      return this.localUpdated !== null ? this.localUpdated : this.attribute.updated;
    },
    displayConfidence() {
      return this.localConfidence !== null ? this.localConfidence : this.attribute.confidence;
    },
    displayReason() {
      return this.localReason !== null ? this.localReason : this.attribute.reason;
    },
    isLlmEnriched() {
      return this.displayDataSource === 'LLM enrichment';
    }
  },

  mounted() {
    // Close popup when clicking outside
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    toggleMetadata() {
      this.showMetadata = !this.showMetadata;

      if (this.showMetadata) {
        this.$nextTick(() => {
          this.positionPopup();
        });
      }
    },

    positionPopup() {
      if (!this.$refs.infoButton || !this.$refs.popup) return;

      const button = this.$refs.infoButton;
      const popup = this.$refs.popup;
      const buttonRect = button.getBoundingClientRect();
      const popupRect = popup.getBoundingClientRect();

      // Calculate position
      let top = buttonRect.bottom + 8; // 8px spacing
      let left = buttonRect.left - (popupRect.width / 2) + (buttonRect.width / 2);

      // Check if popup goes off right edge
      if (left + popupRect.width > window.innerWidth - 16) {
        left = window.innerWidth - popupRect.width - 16;
      }

      // Check if popup goes off left edge
      if (left < 16) {
        left = 16;
      }

      // Check if popup goes off bottom edge
      if (top + popupRect.height > window.innerHeight - 16) {
        // Position above button instead
        top = buttonRect.top - popupRect.height - 8;
      }

      this.popupStyle = {
        top: `${top}px`,
        left: `${left}px`,
      };
    },

    handleClickOutside(event) {
      if (!this.showMetadata) return;

      const popup = this.$refs.popup;
      const button = this.$refs.infoButton;

      if (popup && !popup.contains(event.target) &&
          button && !button.contains(event.target)) {
        this.showMetadata = false;
      }
    },

    startEdit() {
      this.isEditing = true;
      this.error = null;
      this.showMetadata = false;

      // Initialize edit value based on type
      if (this.attribute.field_type === 'json') {
        this.editValueJson = this.displayValue
          ? JSON.stringify(this.displayValue, null, 2)
          : '{}';
      } else if (this.attribute.field_type === 'boolean') {
        this.editValue = !!this.displayValue;
      } else {
        this.editValue = this.displayValue;
      }
    },

    cancelEdit() {
      this.isEditing = false;
      this.editValue = null;
      this.editValueJson = '';
      this.error = null;
    },

    validateJson() {
      if (this.attribute.field_type !== 'json') return true;

      try {
        JSON.parse(this.editValueJson);
        this.error = null;
        return true;
      } catch (e) {
        this.error = 'Invalid JSON';
        return false;
      }
    },

    async save() {
      // Validate before saving
      if (this.attribute.field_type === 'json' && !this.validateJson()) {
        return;
      }

      this.saving = true;
      this.error = null;

      try {
        // Prepare value based on type
        let valueToSave = this.editValue;
        if (this.attribute.field_type === 'json') {
          valueToSave = JSON.parse(this.editValueJson);
        }

        // Call the update_field endpoint
        const response = await axios.post(
          `/api/entities/${this.entityId}/update_field/`,
          {
            field_name: this.attribute.name,
            field_value: valueToSave,
            data_source_name: 'Manual Update',
            reason: 'Updated via UI'
          }
        );

        // Update local state instead of mutating props
        this.localValue = valueToSave;
        this.localDataSource = 'Manual Update';
        this.localUpdated = response.data.updated_at;
        this.localConfidence = 1;  // Reset to 1 for manual updates
        this.localReason = 'Updated via UI';

        // Exit edit mode
        this.isEditing = false;

        // Emit update event with all the new data
        this.$emit('updated', {
          field: this.attribute.name,
          value: valueToSave,
          data_source: 'Manual Update',
          updated: response.data.updated_at,
          confidence: 1,
          reason: 'Updated via UI'
        });

      } catch (error) {
        console.error('Error saving field:', error);
        this.error = error.response?.data?.error || 'Failed to save';
      } finally {
        this.saving = false;
      }
    },

    formatPhone(phone) {
      if (!phone) return '';
      const cleaned = phone.toString().replace(/\D/g, '');
      const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
      }
      return phone;
    },

    formatDate(date) {
      if (!date) return '';
      const [year, month, day] = date.split('T')[0].split('-');
      return new Date(year, month - 1, day).toLocaleDateString();
    },

    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString();
    },

    formatNumber(num) {
      if (num === null || num === undefined) return '';
      return new Intl.NumberFormat().format(num);
    }
  }
};
</script>

<style scoped>
@reference "../../assets/theme.css";
.attribute-field {
  @apply transition-colors hover:bg-gray-50 p-1.5 -mx-1.5 rounded;
}

.attribute-field.llm-enriched {
  @apply bg-blue-50 hover:bg-blue-100;
}
</style>
